import * as R from 'ramda'
import {
  AddContacts,
  Contact,
  EditContact,
  EditContactPayload,
  GetContacts,
  RemoveContacts,
  reqAddContacts,
  reqEditContact,
  reqGetContacts,
  reqRemoveContacts,
} from '@weblite/ts-action/contact'
import { LocalFind } from '@weblite/persist'
import { v4 as uuidv4 } from 'uuid'
import { request } from '$setup/ws'
import { contactsStorage } from '$logic/contacts/storage'
import { getClientUserId } from '$logic/client-user'
import { showUserRemovedFromYourContactSnackbar } from '$comp/snackbars'
import { showContactDialog } from '$comp/modals/contact-dialog'
import { splitByFirst } from '$func/text'
import { isPhoneValidByLib, normalizePhone } from '$func/phone'
import {
  SelectedContacts,
  SelectedContactsPhonesStatus,
} from '$logic/contacts/types'
import { showAddContactsSnackbars } from '$logic/contacts/helper'
import { dateWithOffset } from '$setup/time-offset'

const LOCAL_ID_PREFIX = 'local_'

export const contactsView = contactsStorage.getAll

export const getContact = contactsStorage.get

export function getSignedUpContactIds() {
  contactsStorage.loadSignedUpContacts()
  const contacts = contactsView()
  return R.reject(
    item => R.startsWith(LOCAL_ID_PREFIX, (item as string) || ''),
    R.pluck('id', contacts),
  )
}

export const getNonSignedUpContacts = contactsStorage.getNonSignedUp

export async function saveContacts(contacts: Contact[]) {
  await contactsStorage.deleteByPhones(R.pluck('phone', contacts))
  const userId = getClientUserId()

  await contactsStorage.add(
    contacts
      .filter(({ id }) => id && userId !== id)
      .map(({ id, ...other }) => ({
        ...other,
        id: id || `${LOCAL_ID_PREFIX}${uuidv4()}`,
      })),
  )
}

export async function syncContacts() {
  const updatedAfter = contactsStorage.getLastUpdateTime()

  const res = await request<GetContacts>(reqGetContacts({ updatedAfter }))
  if (res.error) return

  const { contacts, deleted } = res.payload
  if (deleted?.length) await contactsStorage.deleteByPhones(deleted)
  await saveContacts(contacts)
}

export async function addContacts(contacts: EditContactPayload[]) {
  const res = await request<AddContacts>(reqAddContacts({ contacts }))
  if (res.error) return res

  await saveContacts(res.payload.contacts)
  return res
}

export async function editContact(id: string, contact: EditContactPayload) {
  const res = await request<EditContact>(reqEditContact({ contact }))
  if (res.error) return

  const { updatedAt } = res.payload
  await contactsStorage.set(id, { ...contact, updatedAt })
}

export async function deleteContact(id: string) {
  const { phone } = (await getContact(id)) || {}
  if (!phone) return
  const res = await request<RemoveContacts>(
    reqRemoveContacts({ phones: [phone] }),
  )
  if (res.error) return

  showUserRemovedFromYourContactSnackbar(id)
  await contactsStorage.del(id)
}

export async function checkContactPermissionDialog() {
  const lastRejectionTime = LocalFind('lastContactPermissionRejectionTime')
  const oneWeekInMilliSeconds = 3600 * 24 * 7 * 1000

  if (
    !lastRejectionTime ||
    dateWithOffset() - lastRejectionTime > oneWeekInMilliSeconds
  ) {
    showContactDialog()
  }
}

export async function selectContacts() {
  const props: ContactProperty[] = ['name', 'tel']
  const options = { multiple: true }
  const selectedContacts: SelectedContacts = {}
  const phonesStatus: SelectedContactsPhonesStatus = {}

  try {
    const pickedContacts: Pick<ContactInfo, 'name' | 'tel'>[] =
      (await navigator.contacts?.select(props, options)) || []

    const contactsToAdd = R.flatten(
      pickedContacts.map(contact => {
        if (!contact.tel) return null

        const [firstname, lastname] = splitByFirst(
          (contact.name?.[0] || '').trim(),
          ' ',
        )

        const phones = R.uniq(
          contact.tel.map(num => normalizePhone(num.replace(/\s/g, ''))),
        )

        selectedContacts[
          firstname
            ? `${firstname}${lastname ? ` ${lastname}` : ''}`
            : contact.tel[0]
        ] = phones

        return phones.map(phone => {
          if (!isPhoneValidByLib(phone)) {
            phonesStatus[phone] = 'noAppAccount'
            return null
          }

          return { phone, firstname: firstname || phone, lastname }
        })
      }),
    ).filter(Boolean) as EditContactPayload[]

    if (contactsToAdd.length === 0) {
      showAddContactsSnackbars(selectedContacts, phonesStatus)
      return
    }

    const res = await addContacts(contactsToAdd)
    res.payload?.contacts.map(contact => {
      phonesStatus[contact.phone] = contact.id
        ? 'addedToContacts'
        : 'noAppAccount'
    })

    showAddContactsSnackbars(selectedContacts, phonesStatus)
  } catch (err) {
    //
  }
}
